'use client'

import { storyblokEditable } from '@storyblok/react/rsc'
import {
  type ChangeEvent,
  type FC,
  type InvalidEvent,
  useId,
  useState,
} from 'react'
import { useIntl } from 'react-intl'

import { Button, Checkbox, FormHelperText } from 'base-ui'

import { useGlobalConfigContext } from '../../../global-config'
import { TARGET_PAGE_REDIRECT_PARAM } from '../../../login-registration'
import {
  PUBLIC_API_PREFIX,
  urlAddSearchParams,
  urlResolverGetAccountRedpoints,
} from '../../../url-handling'
import { type StoryblokComponentProps } from '../../model'
import { type StoryblokRedPointsSignUpFormType } from '../../storyblok'
import { useCmsRichTextRender } from '../../useCmsRichTextRender'

const ACCOUNT_REDPOINTS_INTERESTED_ENDPOINT_URL = `${PUBLIC_API_PREFIX}/api/redpoints/interested`

type StoryblokRedPointsSignUpFormContentProps =
  StoryblokComponentProps<StoryblokRedPointsSignUpFormType>

export const StoryblokRedPointsSignUpFormContent: FC<
  StoryblokRedPointsSignUpFormContentProps
> = ({ blok }) => {
  const renderRichText = useCmsRichTextRender()
  const intl = useIntl()
  const globalConfig = useGlobalConfigContext()

  const { buttonText, checkboxRequired = true, text } = blok
  const accountRedpointsUrl = urlResolverGetAccountRedpoints(globalConfig)
  const submitUrl = urlAddSearchParams(
    ACCOUNT_REDPOINTS_INTERESTED_ENDPOINT_URL,
    {
      [TARGET_PAGE_REDIRECT_PARAM]: accountRedpointsUrl,
    },
  )

  const checkboxLabelId = useId()
  const [checkboxError, setCheckboxError] = useState<string>()

  const handleFormInvalid = (event: InvalidEvent<HTMLFormElement>): void => {
    event.preventDefault()
    setCheckboxError(
      intl.formatMessage({
        id: 'account.redpoints.participation.confirmSelection',
      }),
    )
  }

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      setCheckboxError(undefined)
    }
  }

  return (
    <form
      {...storyblokEditable(blok)}
      action={submitUrl}
      className="rounded-m bg-light-primary-low p-4 shadow-m"
      method="POST"
      onInvalid={handleFormInvalid}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <div className="flex items-start gap-2">
            <Checkbox
              aria-labelledby={checkboxLabelId}
              data-qa-id="redpoints-lp-registration-checkbox"
              onChange={handleCheckboxChange}
              required={checkboxRequired}
            />
            <div className="prose flex-1" id={checkboxLabelId}>
              {renderRichText(text)}
            </div>
          </div>
          {checkboxError ? (
            <FormHelperText
              data-qa-id="redpoints-lp-registration-checkbox-error"
              error
            >
              {checkboxError}
            </FormHelperText>
          ) : null}
        </div>
        <Button
          className="inline-flex tablet:w-fit"
          data-qa-id="red-points-signup-form-submit-button"
          size="medium"
          type="submit"
        >
          {buttonText}
        </Button>
      </div>
    </form>
  )
}
